import React, { useState } from 'react';
import Highcharts from 'highcharts';
import { useSelector } from 'react-redux';

import { ReduxStates } from '../../../../redux/reduxTypes';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsWrapper from '../../../../components/HighchartsWrapper';
import { baseGraphConfigs } from './configs';
import { intToString } from '../../../../helpers/format';
import GraphLegend from '../../../../components/GraphLegend';

// extends export functionality to the main highcharts library
if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
}

interface GlanceGraphData {
  date: string;
  currentYearValue: number;
  planValue: number;
  digitalPenetrationValue: number;
}

interface Props {
  currencyCode?: string;
  lineGraphEl: any;
  graphData: any;
  showDownloadOptions: boolean;
  setShowDownloadOptions: (value: React.SetStateAction<boolean>) => void;
}

const legends: Highcharts.SeriesOptionsType[] = [
  {
    id: 'graph_current_year',
    type: 'column',
    color: '#F7B74D',
    name: 'graph_current_year',
    data: [5, 18, 3, 5]
  },
  {
    id: 'graph_plan',
    type: 'column',
    color: '#4D70B8',
    name: 'graph_plan',
    data: [1, 3, 10, 2]
  },
  {
    id: 'graph_digital_penetration',
    type: 'line',
    color: '#141414',
    name: 'graph_digital_penetration',
    data: []
  }
];

const Graph = ({ currencyCode, lineGraphEl, graphData }: Props): React.ReactElement => {
  const copyData = useSelector((state: ReduxStates) => state.copyData);
  const [showLabels, setShowLabels] = useState(true);

  const handleValue = (value: number | string | undefined) => {
    //create new number value for valid number testing, because type in Highcharts can be a string or number
    const vString = typeof value === 'undefined' ? '' : value.toString();
    const v: number = parseFloat(vString);

    if ((!value && value !== 0) || value === Number.MAX_VALUE || !isFinite(v)) return '--';
    const withoutUnit: any = intToString(Number(value), 1, false, currencyCode);
    const withUnit = `${currencyCode}${withoutUnit}`;
    return withUnit;
  };

  const formatTooltip = (points: any | undefined, xValue: number) => {
    const combinedGraph: Highcharts.Chart = lineGraphEl?.current?.chart;
    if (points && combinedGraph) {
      const pointsById = (id: string) => {
        const idPoints = points.filter((point: any) => point?.series?.options?.id === id);
        return idPoints;
      };

      const cySeries: any = combinedGraph.get('graph_current_year');
      const planSeries: any = combinedGraph.get('graph_plan');
      const dpSeries: any = combinedGraph.get('graph_digital_penetration');

      const cyPoint = pointsById('graph_current_year')[0];
      const planPoint = pointsById('graph_plan')[0];
      const dpPoint = pointsById('graph_digital_penetration')[0];

      const formattedPoints = `
        <b>${xValue}</b>
        <br/>
        ${
          cySeries?.visible
            ? `<span style="color:#F7B74D">\u25CF </span> &nbsp;
          <span>${copyData?.graph_current_year}: <b>${handleValue(cyPoint?.y)}</b></span>
          <br/>
        `
            : ``
        }
        ${
          planSeries?.visible
            ? `<span style="color:#4D70B8">\u25CF </span> &nbsp;
          <span>${copyData?.graph_plan}: <b>${handleValue(planPoint?.y)}</b></span>
          <br/>
        `
            : ``
        }
        ${
          dpSeries?.visible
            ? `<span style="color:#141414">\u25CF </span> &nbsp;
          <span>${copyData?.graph_digital_penetration}: <b>${dpPoint?.y ? dpPoint?.y + '%' : '--'}</b></span>
          <br/>
        `
            : ``
        }
      `;

      return formattedPoints;
    } else {
      return '';
    }
  };

  const graphOptions: Highcharts.Options = {
    ...baseGraphConfigs,
    plotOptions: {
      ...baseGraphConfigs.plotOptions,
      column: {
        dataLabels: {
          enabled: showLabels,
          formatter: function () {
            return handleValue(this.y);
          }
        },
        pointPadding: 0
      },
      line: {
        dataLabels: {
          enabled: showLabels,
          y: 22,
          formatter: function () {
            return this.y + '%';
          }
        }
      },
      series: {
        states: {
          inactive: {
            opacity: 1
          }
        }
      }
    },
    tooltip: {
      // formatter: renderTooltip,
      formatter: function () {
        return formatTooltip(this.points, this.x);
      },
      enabled: true,
      shared: true
    },
    xAxis: {
      labels: {
        enabled: true
      },
      categories: graphData?.map((g: GlanceGraphData) => g.date) || []
    },
    yAxis: [
      {
        labels: {
          formatter: function () {
            return handleValue(this.value);
          }
        },
        title: undefined,
        opposite: false
      },
      {
        labels: {
          format: '{value}%'
        },
        title: undefined,
        opposite: true
      }
    ],
    series: [
      {
        type: 'column',
        color: '#F7B74D',
        data:
          graphData?.map((g: GlanceGraphData) => {
            return {
              y: g.currentYearValue,
              color: '#F7B74D',
              borderColor: '#F7B74D'
            };
          }) || [],
        name: copyData?.graph_current_year,
        id: 'graph_current_year'
      },
      {
        type: 'column',
        color: '#4D70B8',
        data:
          graphData?.map((g: GlanceGraphData) => {
            return {
              y: g.planValue,
              color: '#4D70B8',
              borderColor: '#4D70B8'
            };
          }) || [],
        name: copyData?.graph_plan,
        id: 'graph_plan'
      },
      {
        type: 'line',
        color: '#141414',
        yAxis: 1,
        data: graphData?.map((g: GlanceGraphData) => g.digitalPenetrationValue) || [],
        name: copyData?.graph_digital_penetration,
        id: 'graph_digital_penetration'
      }
    ]
  };

  const toggleVisibility = (visible: boolean, id: string) => {
    const combinedGraph: Highcharts.Chart = lineGraphEl?.current?.chart;
    const series: any = combinedGraph?.get(id);
    visible ? series?.show() : series?.hide();
  };

  return (
    <>
      <div className="relative">
        {graphData ? (
          <>
            <div className="mb-5 flex items-center justify-end mr-3">
              <input
                className="h-4 w-4 border-gray-300 rounded-sm mr-2 cursor-pointer"
                type="checkbox"
                checked={showLabels ? true : false}
                onClick={() => setShowLabels(!showLabels)}
              />
              <label className="text-fontBlock text-sm">Callouts</label>
            </div>
            <HighchartsWrapper
              lineGraphEl={lineGraphEl}
              containerStyle={{ height: '100%', borderBottomRightRadius: `4px` }}
              graphOptions={graphOptions}
            />
          </>
        ) : (
          <div className="w-full h-full min-h-[200px] flex items-center justify-center">
            <p className="font-nunitoBold text-filterList text-base">{copyData?.no_data}</p>
          </div>
        )}
        {copyData ? (
          <div className="w-full flex justify-center my-1">
            {legends?.map((legend, idx) => (
              <GraphLegend
                key={idx}
                legend={legend}
                label={copyData[legend.name as string]}
                toggleVisibility={(value) => toggleVisibility(value, legend.id || '')}
                tooltipHelpText={copyData[(legend.name as string) + '_tooltip_helptext']}
              />
            ))}
          </div>
        ) : (
          ''
        )}
      </div>
    </>
  );
};

export default Graph;
